/* useCascadeEngine — Phase C · C3c-β direct wire-in (engine is sole driver)
 *
 * Thin React hook that fires POST /api/rules-engine/evaluate whenever
 * the appraisal's anchor tuple (roof_type, brand, quote_type, ...)
 * changes, caches the result in a module-level Map keyed by the
 * stringified anchor tuple, and returns the full cascade + validation
 * error set for consumers to drive the Components form.
 *
 * As of C3c-β (2026-02c · go-live rebuild), the legacy
 * `inferMaterialFromContext` fallback in AppraisalComponentsTab has
 * been deleted. When the engine returns null (still loading OR no
 * matching rule for the anchor tuple), callers leave the row blank
 * rather than guessing — engine is single source of truth.
 *
 * Cache is process-lifetime · cleared on hard reload. Since anchor
 * tuples on a single appraisal are stable, this typically fires ONCE
 * per appraisal open · zero API pressure.
 */
import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const _cache = new Map();          // key = JSON.stringify(anchors) → EvaluationResult
const _inflight = new Map();       // key → Promise · dedupes concurrent fires
const DEBOUNCE_MS = 150;

function normaliseAnchors(a) {
  const clean = {};
  for (const [k, v] of Object.entries(a || {})) {
    if (v !== null && v !== undefined && String(v).trim() !== '') clean[k] = v;
  }
  return clean;
}

export async function evaluateCascade(anchors, context = null) {
  const clean = normaliseAnchors(anchors);
  const key = JSON.stringify({ a: clean, c: context || null });
  if (_cache.has(key)) return _cache.get(key);
  if (_inflight.has(key)) return _inflight.get(key);
  const p = (async () => {
    try {
      const body = { anchors: clean };
      if (context) body.context = context;
      const res = await fetch(`${API}/api/rules-engine/evaluate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`engine ${res.status}`);
      const data = await res.json();
      _cache.set(key, data);
      return data;
    } catch (e) {
      // Cache the null so we don't retry every render · safety-net kicks in
      _cache.set(key, null);
      return null;
    } finally {
      _inflight.delete(key);
    }
  })();
  _inflight.set(key, p);
  return p;
}

export function useCascadeEngine(anchors, context = null) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const clean = normaliseAnchors(anchors);
  const key = JSON.stringify({ a: clean, c: context || null });

  useEffect(() => {
    // Cache-hit fast-path · no state churn
    if (_cache.has(key)) {
      setResult(_cache.get(key));
      setLoading(false);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      evaluateCascade(clean, context).then(r => {
        setResult(r);
        setLoading(false);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps

  return {
    result,
    loading,
    engineMaterial: result?.cascade?.material ?? null,
    engineBrands:   result?.cascade?.brands ?? null,
    engineProfiles: result?.cascade?.profiles ?? null,
    // C4-audit remediation (2026-02c) — `engineSectionMaterial` used
    // to expose `cascade.section_material` populated by MTS-* rules.
    // Both were de-scoped once we confirmed no frontend site consumed
    // them (the imperative `detectMaterialFor` helper does the same
    // job per row).
    engineRatePicks:       result?.cascade?.rate_picks ?? null,
    validationErrors:      result?.validation_errors ?? [],
    hasBlockingErrors: (result?.validation_errors || [])
      .some(e => (e?.severity || 'error') === 'error'),
    engineVersionHash: result?.rule_version_hash ?? '',
    // C4h (2026-02c) — surface engine health telemetry so the parent
    // can render an "engine active" badge next to the Components header.
    // C4-P0.2 (2026-02c) — backend serialises as `trace` (not
    // `rule_traces`); the mismatch was making the badge stuck at
    // "Engine · idle · 0/0" even when rules fired.
    rulesFired: (result?.trace || []).filter(t => t?.matched).length,
    rulesEvaluated: (result?.trace || []).length,
  };
}

// Manual cache clear · exposed for test harness + admin invalidate button
export function clearCascadeCache() {
  _cache.clear();
  _inflight.clear();
}
